import {
    type ClipboardEvent,
    type FocusEvent,
    type KeyboardEvent,
    useCallback,
    useId,
    useRef,
    useState,
} from "react";
import { XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { validateDomain } from "./validateDomain";

export interface IAllowedDomainsEditorProps {
    /** Current list of allowed domains. Empty list = all external recipients blocked. */
    value: string[];
    /** Replace the list. Never mutated in place. */
    onChange: (next: string[]) => void;
    disabled?: boolean;
    /** Id of an external helper text node, wired into the input's
     * `aria-describedby`. */
    helperTextId?: string;
}

export function AllowedDomainsEditor({
    value,
    onChange,
    disabled,
    helperTextId,
}: IAllowedDomainsEditorProps) {
    const internalHelperId = useId();
    const inputId = useId();
    const errorId = useId();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const [draft, setDraft] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    // Announcement text (not boolean) so AT re-announces on repeated remove
    // of the same chip name.
    const [statusMessage, setStatusMessage] = useState("");

    const handleRemove = useCallback(
        (domain: string) => {
            if (disabled) {
                return;
            }
            onChange(value.filter((entry) => entry !== domain));
            setStatusMessage(`Removed ${domain}`);
            inputRef.current?.focus();
        },
        [disabled, onChange, value]
    );

    const handleAdd = useCallback(() => {
        if (disabled) {
            return;
        }
        const result = validateDomain(draft, value);
        if (!result.ok) {
            setErrorMessage(result.reason);
            return;
        }
        onChange([...value, result.value]);
        setStatusMessage(`Added ${result.value}`);
        setDraft("");
        setErrorMessage("");
        inputRef.current?.focus();
    }, [disabled, draft, onChange, value]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                handleAdd();
            }
        },
        [handleAdd]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            // Flush a typed-but-uncommitted draft when focus leaves the
            // editor (e.g. user clicks "Save Settings" without pressing
            // Enter). Skip when focus moves to a control inside the editor;
            // those own their own commit, and a duplicate flush would
            // surface a spurious "Enter a domain" error after a clean add.
            const next = event.relatedTarget;
            if (next instanceof Element && editorRef.current?.contains(next)) {
                return;
            }
            if (!disabled && draft.trim() !== "") {
                handleAdd();
            }
        },
        [disabled, draft, handleAdd]
    );

    const handlePaste = useCallback(
        (event: ClipboardEvent<HTMLInputElement>) => {
            if (disabled) {
                return;
            }
            const pasted = event.clipboardData.getData("text");
            // Only multi-token pastes (commas, semicolons, whitespace) trigger
            // split-and-commit; single-token pastes fall through to default.
            if (!/[\s,;]/.test(pasted)) {
                return;
            }

            const tokens = pasted
                .split(/[\s,;]+/)
                .map((t) => t.trim())
                .filter(Boolean);

            // Separators only (e.g. a stray ","); fall through to default
            // paste rather than preventing it and clobbering the user's
            // typed-but-uncommitted draft with nothing added in return.
            if (tokens.length === 0) {
                return;
            }
            event.preventDefault();

            const accepted: string[] = [];
            const rejected: { token: string; reason: string }[] = [];
            // Stage incrementally so duplicates within one paste are caught
            // here (not silently dropped by the backend dedupe).
            const stagedView = [...value];
            for (const token of tokens) {
                const result = validateDomain(token, stagedView);
                if (result.ok) {
                    accepted.push(result.value);
                    stagedView.push(result.value);
                } else {
                    rejected.push({ token, reason: result.reason });
                }
            }

            if (accepted.length > 0) {
                onChange([...value, ...accepted]);
                setStatusMessage(`Added ${accepted.join(", ")}`);
            }

            if (rejected.length > 0) {
                setErrorMessage(
                    `Could not add: ${rejected
                        .map(({ reason }) => reason)
                        .join(" ")}`
                );
            } else {
                setErrorMessage("");
            }

            setDraft("");
        },
        [disabled, onChange, value]
    );

    const isEmpty = value.length === 0;
    // Only reference IDs that actually render; the empty-state helper is
    // conditional, so a dangling reference would resolve to nothing.
    const describedBy = [
        helperTextId ?? null,
        !helperTextId && isEmpty ? internalHelperId : null,
        errorMessage ? errorId : null,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div
            ref={editorRef}
            className={cn(
                "border-input bg-background flex flex-col gap-3 rounded-md border p-3",
                disabled && "opacity-50"
            )}
            data-slot="allowed-domains-editor"
        >
            <div className="flex flex-col gap-0.5">
                <div className="text-sm font-medium">
                    Allowed Recipient Domains
                </div>
                <p className="text-muted-foreground text-sm">
                    Restrict who can receive shared Polly conversations from
                    your organization. Leave empty to block all external
                    recipients.
                </p>
            </div>

            {isEmpty ? (
                <p
                    id={internalHelperId}
                    className="text-muted-foreground text-sm"
                >
                    All external recipients blocked. Add a domain (e.g.
                    acme.com) to allow sharing with that domain.
                </p>
            ) : (
                <ul
                    aria-label="Allowed domains"
                    className="flex flex-wrap gap-2"
                >
                    {value.map((domain) => (
                        <li key={domain} className="contents">
                            <Badge variant="secondary" className="gap-1 pr-1">
                                <span>{domain}</span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${domain}`}
                                    disabled={disabled}
                                    onClick={() => handleRemove(domain)}
                                    className="hover:bg-foreground/10 focus-visible:ring-ring/50 -mr-0.5 inline-flex size-4 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                                >
                                    <XIcon className="size-3" />
                                </button>
                            </Badge>
                        </li>
                    ))}
                </ul>
            )}

            {/* `<div>`, not `<form>`: nests inside the outer settings form
             * without producing invalid DOM or bubbling submits. */}
            <div className="flex flex-col gap-2">
                <Label htmlFor={inputId} className="sr-only">
                    Add domain
                </Label>
                <div className="flex gap-2">
                    <Input
                        ref={inputRef}
                        id={inputId}
                        type="text"
                        placeholder="acme.com"
                        value={draft}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            if (errorMessage) {
                                setErrorMessage("");
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onBlur={handleBlur}
                        disabled={disabled}
                        aria-invalid={errorMessage ? true : undefined}
                        aria-describedby={describedBy || undefined}
                    />
                    <Button
                        type="button"
                        onClick={() => handleAdd()}
                        disabled={disabled}
                        variant="secondary"
                    >
                        Add
                    </Button>
                </div>
                {errorMessage && (
                    <p
                        id={errorId}
                        role="alert"
                        className="text-destructive text-sm"
                    >
                        {errorMessage}
                    </p>
                )}
            </div>

            <div role="status" aria-live="polite" className="sr-only">
                {statusMessage}
            </div>
        </div>
    );
}
